/**
 * Client scope — read from the URL, and only from the URL.
 *
 * ND-C1 (owner, 2026-09-07): a client-owned screen lives at
 * `/clients/:id/<screen>`. There is no persisted "current client", and this
 * module must never become one. The cache below is a *request* cache keyed by
 * the id already in the path — it answers "what is this client called", never
 * "which client am I looking at". That second question has exactly one answer,
 * and it is the address bar.
 *
 * Why that matters here rather than in a comment somewhere: the most expensive
 * mistake available in this product is right numbers, wrong client. A stored
 * current-client makes that mistake invisible in a screenshot, a bug report and
 * a second browser tab.
 */

import { useEffect, useState } from 'react'
import { useMatches } from 'react-router'

import { ApiError, type ClientRecord, clients } from './api'

/** Screens that live inside a client. The order is the sidebar's. */
export const CLIENT_SCREENS = [
  'intake',
  'review',
  'returns',
  'gstr3b',
  'reconciliation',
  'filed-compare',
  'tb-recon',
  'reports',
  'download',
  'activity',
  'filing',
] as const

export type ClientScreen = (typeof CLIENT_SCREENS)[number]

export function clientPath(id: string, screen?: ClientScreen): string {
  return screen ? `/clients/${id}/${screen}` : `/clients/${id}`
}

/**
 * The client id in the current URL, or null when we are not inside a client.
 * `useMatches` rather than `useParams` so the chrome — which renders *above*
 * the client routes — can read it too.
 */
export function useClientId(): string | null {
  const matches = useMatches()
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const params = matches[i]?.params as Record<string, string | undefined> | undefined
    if (params?.id) return params.id
  }
  return null
}

// One in-flight/settled read per id, shared by the chrome and the layout so a
// single navigation does not fetch the same client twice.
const cache = new Map<string, Promise<ClientRecord>>()
const listeners = new Set<(id: string) => void>()

/**
 * Forget a client and tell everything showing it to read it again.
 *
 * **It had no caller until 2026-09-29 (N5)**, and deleting the cache entry alone changed
 * nothing on screen: after **Save changes** or **Archive** on Client Details, the context strip
 * above every client screen kept the old name and no *Archived* chip until a full reload.
 */
export function invalidateClient(id: string): void {
  cache.delete(id)
  for (const listener of listeners) listener(id)
}

/** Called with the id whenever `invalidateClient` runs. Returns the unsubscribe. */
export function onClientInvalidated(listener: (id: string) => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function read(id: string): Promise<ClientRecord> {
  let hit = cache.get(id)
  if (!hit) {
    hit = clients.get(id).then((body) => body.data)
    cache.set(id, hit)
    // A failed read must not be remembered as a failure for the life of the
    // tab — a 500 during a deploy would otherwise persist past the fix.
    hit.catch(() => cache.delete(id))
  }
  return hit
}

export interface ScopedClient {
  client: ClientRecord | null
  loading: boolean
  /** The id is well-formed but no such client is ours — NOT_FOUND, by design. */
  missing: boolean
  error: string | null
}

export function useScopedClient(): ScopedClient {
  const id = useClientId()
  const [state, setState] = useState<ScopedClient>({
    client: null,
    loading: Boolean(id),
    missing: false,
    error: null,
  })

  // Bumped when this client is invalidated, to read it again.
  const [revision, setRevision] = useState(0)
  useEffect(
    () => onClientInvalidated((changed) => {
      if (changed === id) setRevision((r) => r + 1)
    }),
    [id],
  )

  useEffect(() => {
    if (!id) {
      setState({ client: null, loading: false, missing: false, error: null })
      return
    }
    let live = true
    // A re-read after an edit keeps showing the client it has — flipping to "Loading…" would
    // unmount every screen under the layout just to change a name in the strip.
    setState((s) =>
      revision > 0 && s.client?.id === id
        ? s
        : { ...s, loading: true, missing: false, error: null },
    )
    read(id)
      .then((client) => {
        if (live) setState({ client, loading: false, missing: false, error: null })
      })
      .catch((err: unknown) => {
        if (!live) return
        const missing = err instanceof ApiError && err.code === 'NOT_FOUND'
        setState({
          client: null,
          loading: false,
          missing,
          error: missing ? null : err instanceof ApiError ? err.message : 'That client could not be loaded.',
        })
      })
    return () => {
      live = false
    }
  }, [id, revision])

  return state
}
